import { useState } from "react";
import { Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { mediaUrl } from "../../context/ContentContext";
import { useAdminAuth } from "../../context/AdminAuthContext";

const LONG_KEYS = ["body", "description", "subtitle", "disclaimer", "note", "missionp1", "missionp2", "a", "herosubtitle", "evaldescription"];

function labelize(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/\bCta\b/g, "CTA")
    .replace(/\bP1\b/, "Paragraph 1")
    .replace(/\bP2\b/, "Paragraph 2");
}

function ImageField({ value, onChange }) {
  const { authAxios } = useAdminAuth();
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authAxios.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <div className="h-24 w-32 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        {value ? <img src={mediaUrl(value)} alt="preview" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-slate-300"><ImageIcon size={22} /></div>}
      </div>
      <div className="flex-1">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="h-10 w-full rounded-lg border border-slate-200 px-3 text-[13px] outline-none focus:border-[var(--brand-blue)]" />
        <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-semibold text-[var(--ink)] hover:bg-slate-50">
          <Upload size={14} /> {uploading ? "Uploading..." : "Upload image"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
}

function Primitive({ fieldKey, value, onChange, showLabel = true }) {
  const keyLower = (fieldKey || "").toLowerCase();
  if (keyLower.includes("image")) {
    return (
      <div className="mb-5">
        {showLabel && <label className="mb-1.5 block text-[13px] font-semibold text-[var(--ink)]">{labelize(fieldKey)}</label>}
        <ImageField value={value} onChange={onChange} />
      </div>
    );
  }
  const long = LONG_KEYS.includes(keyLower) || (typeof value === "string" && value.length > 90);
  return (
    <div className="mb-5">
      {showLabel && <label className="mb-1.5 block text-[13px] font-semibold text-[var(--ink)]">{labelize(fieldKey)}</label>}
      {long ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px] leading-relaxed outline-none focus:border-[var(--brand-blue)]" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-lg border border-slate-200 px-3 text-[14px] outline-none focus:border-[var(--brand-blue)]" />
      )}
    </div>
  );
}

function StringList({ fieldKey, value, onChange }) {
  const add = () => onChange([...value, ""]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, v) => onChange(value.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="mb-6">
      <label className="mb-2 block text-[13px] font-bold uppercase tracking-wide text-slate-500">{labelize(fieldKey)}</label>
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={item} onChange={(e) => update(i, e.target.value)} className="h-10 flex-1 rounded-lg border border-slate-200 px-3 text-[14px] outline-none focus:border-[var(--brand-blue)]" />
            <button onClick={() => remove(i)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-[13px] font-semibold text-[var(--brand-blue)] hover:bg-blue-50"><Plus size={15} /> Add item</button>
    </div>
  );
}

function ObjectList({ fieldKey, value, onChange }) {
  const keys = value[0] ? Object.keys(value[0]) : [];
  const add = () => onChange([...value, Object.fromEntries(keys.map((k) => [k, ""]))]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const updateField = (i, k, v) => onChange(value.map((item, idx) => (idx === i ? { ...item, [k]: v } : item)));
  return (
    <div className="mb-6">
      <label className="mb-2 block text-[13px] font-bold uppercase tracking-wide text-slate-500">{labelize(fieldKey)}</label>
      <div className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12px] font-bold text-slate-400">#{i + 1}</span>
              <button onClick={() => remove(i)} className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
            </div>
            {Object.keys(item).map((k) => (
              <Primitive key={k} fieldKey={k} value={item[k]} onChange={(v) => updateField(i, k, v)} />
            ))}
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-[13px] font-semibold text-[var(--brand-blue)] hover:bg-blue-50"><Plus size={15} /> Add item</button>
    </div>
  );
}

function renderField(key, val, onChange) {
  if (Array.isArray(val)) {
    if (val.length === 0 || typeof val[0] === "string") {
      return <StringList key={key} fieldKey={key} value={val} onChange={onChange} />;
    }
    return <ObjectList key={key} fieldKey={key} value={val} onChange={onChange} />;
  }
  if (val && typeof val === "object") {
    return (
      <div key={key} className="mb-5 rounded-xl border border-slate-200 p-4">
        <p className="mb-3 text-[13px] font-bold uppercase tracking-wide text-slate-500">{labelize(key)}</p>
        {Object.keys(val).map((k) => renderField(k, val[k], (v) => onChange({ ...val, [k]: v })))}
      </div>
    );
  }
  return <Primitive key={key} fieldKey={key} value={val} onChange={onChange} />;
}

export default function SectionEditor({ fieldKey, value, onChange }) {
  // Top-level section is either an array or an object of fields.
  if (Array.isArray(value)) {
    if (value.length === 0 || typeof value[0] === "string") {
      return <StringList fieldKey={fieldKey} value={value} onChange={onChange} />;
    }
    return <ObjectList fieldKey={fieldKey} value={value} onChange={onChange} />;
  }
  if (value && typeof value === "object") {
    return <>{Object.keys(value).map((k) => renderField(k, value[k], (v) => onChange({ ...value, [k]: v })))}</>;
  }
  return <Primitive fieldKey={fieldKey} value={value} onChange={onChange} />;
}
